import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import "./App.css";
import {
 BrowserRouter,
  Routes,
  Route,
  useLocation,
  useNavigate,} from "react-router-dom";
import axios from "axios";

// Components
import Header from "./components/Header";
import HeroSection from "./components/HeroSection";
import RecognitionSection from "./components/RecognitionSection";


import ApproachSection from "./components/ApproachSection";
import DomainsSection from "./components/DomainsSection";
import WhyChooseSection from "./components/WhyChooseSection";
import JourneySection from "./components/JourneySection";
import AboutSection from "./components/AboutSection";
import InvestmentSection from "./components/InvestmentSection";
import EditorialCarousel from "./components/EditorialCarousel";
import FirstStepSection from "./components/FirstStepSection";
import ContactSection from "./components/ContactSection";
import Footer from "./components/Footer";
import Impressum from "./components/Impressum";
import Datenschutz from "./components/Datenschutz";
import AGB from "./components/AGB";
import Widerruf from "./components/Widerruf";
import Cookies from "./components/Cookies";
import ScrollToTopButton from "./components/ScrollToTopButton";



// ⬇️ nouvelles pages de services
import ArtEtreSoi from "./components/ArtEtreSoi";
import AubeParentale from "./components/AubeParentale";
import ArchitecteAncrage from "./components/ArchitecteAncrage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ---------------------------------------------------------------------
// Helpers langue
// ---------------------------------------------------------------------

// ⬅︎ NEU: erlaubte Sprachen + Normalisierung
const OK_LNGS = ["fr", "ar", "de"]; // ⬅︎ NEU
const normLng = (lng) => {
  if (!lng) return "fr";
  const short = String(lng).slice(0, 2).toLowerCase();
  return OK_LNGS.includes(short) ? short : "fr";
};


const applyHtmlAttrs = (lngLike) => {
  const lng = normLng(lngLike); // ⬅︎ CHANGE
  document.documentElement.lang = lng;
  document.documentElement.dir = lng === "ar" ? "rtl" : "ltr"; // bleibt pragmatisch
};

// Hook partagé pour synchroniser i18n <-> ?lang=
const useSyncLangWithUrl = () => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  // → lecture du ?lang= au chargement + set i18n
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    let urlLang = params.get("lang");

    if (!urlLang) {
      const stored =
        localStorage.getItem("lang") || localStorage.getItem("i18nextLng"); // ⬅︎ NEU
      urlLang = normLng(stored); // ⬅︎ CHANGE (de zulassen)
      params.set("lang", urlLang);

      navigate(
        {
          pathname: location.pathname,
          search: `?${params.toString()}`,
          hash: location.hash,
        },
        { replace: true }
      );
    }

   if (urlLang && normLng(urlLang) !== normLng(i18n.language)) {
      i18n.changeLanguage(normLng(urlLang)); // ⬅︎ CHANGE
    }


     applyHtmlAttrs(urlLang || i18n.language);
    if (urlLang) {
      const v = normLng(urlLang); // ⬅︎ NEU
      localStorage.setItem("lang", v); // ⬅︎ NEU
      localStorage.setItem("i18nextLng", v); // ⬅︎ NEU
    }
  }, [location.search, location.pathname, location.hash, i18n, navigate]);

  // → si la langue change via le switcher, on met à jour l’URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const current = params.get("lang");

    if (i18n.language && current !== i18n.language) {
      params.set("lang", i18n.language);
      navigate(
        {
          pathname: location.pathname,
          search: `?${params.toString()}`,
          hash: location.hash, 
        },
        { replace: true }
      );
      applyHtmlAttrs(i18n.language);
    }
  }, [i18n.language, location.search, location.pathname, location.hash, navigate]);

  return { i18n };
};
// ---------------------------------------------------------------------
// Scroll automatique en haut à chaque changement de page
// (About, Investment, ArtEtreSoi, etc.)
// ---------------------------------------------------------------------

const ScrollToTopOnRoute = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    // Wenn ein Hash da ist (/#accueil, /#philosophie etc.), 
    // lassen wir den Anker in Ruhe
    if (hash) return;

    // Für "normale" Seiten: immer nach oben
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "auto", // oder "smooth", wenn du animiert willst
    });
  }, [pathname, hash]);

  return null;
};


// ---------------------------------------------------------------------
// Layout principal utilisé par la home ET les pages services
// ---------------------------------------------------------------------

const BaseLayout = ({ children }) => {
  const { i18n } = useSyncLangWithUrl();

  // Saubere Language/Dir
  const htmlLang = normLng(i18n.language);
  const htmlDir = htmlLang === "ar" ? "rtl" : "ltr";

  // Canonical/hreflang pro aktueller Seite
  const { origin: baseUrl, pathname, search, hash } = window.location;
  const params = new URLSearchParams(search);
  if (!params.has("lang")) params.set("lang", htmlLang);
  const canonical = `${baseUrl}${pathname}?${params.toString()}${hash || ""}`;

  const hrefFr = `${baseUrl}${pathname}?${new URLSearchParams({ ...Object.fromEntries(params), lang: "fr" }).toString()}`;
  const hrefAr = `${baseUrl}${pathname}?${new URLSearchParams({ ...Object.fromEntries(params), lang: "ar" }).toString()}`;
  const hrefDe = `${baseUrl}${pathname}?${new URLSearchParams({ ...Object.fromEntries(params), lang: "de" }).toString()}`;

  return (
    <div className="min-h-screen bg-cream-white">
      <Helmet>
        {/* html lang/dir wirklich setzen */}
        <html lang={htmlLang} dir={htmlDir} />

        {/* Canonical immer mit ?lang= und mit aktueller Route */}
        <link rel="canonical" href={canonical} />

        {/* hreflang je Route */}
        <link rel="alternate" hrefLang="fr" href={hrefFr} />
        <link rel="alternate" hrefLang="de" href={hrefDe} />
        <link rel="alternate" hrefLang="ar" href={hrefAr} />
        <link rel="alternate" hrefLang="x-default" href={hrefFr} />

        {/* Kurzbeschreibung je Sprache */}
        <meta
          name="description"
          content={
            htmlLang === "ar"
              ? "Mind'UP  سناء أيت بلا - تدريب منظومي وإنساني."
              : htmlLang === "de"
              ? "Mind’UP by Sanaa Ait Balla – Systemisches, interkulturelles Coaching: klar, menschlich, wirksam."
              : "Mind'UP by Sanaa Ait Balla - Coaching systémique et interculturel."
          }
        />
      </Helmet>

      <Header />
      <main>{children}</main>
      <Footer />
      <ScrollToTopButton />
    </div>
  );
};
// ---------------------------------------------------------------------
// Home (landing complète)
// ---------------------------------------------------------------------

const HomeInner = () => {
  useEffect(() => {
    const helloWorldApi = async () => {
      try {
        const response = await axios.get(`${API}/`);
        console.log(response.data.message);
      } catch (e) {
        console.error(e, `errored out requesting / api`);
      }
    };
    helloWorldApi();
  }, []);

  return (
    <BaseLayout>
    <div className="space-y-1 md:space-y-1">
      <HeroSection />
      <RecognitionSection />
      <DomainsSection />
      {/* About & Investment ont été retirés de la home */}
      <WhyChooseSection />
      <JourneySection />
      <EditorialCarousel />
      <FirstStepSection />
      <ContactSection />
       </div>
    </BaseLayout>
  );
};

// ---------------------------------------------------------------------
// Pages services : même layout que la home
// ---------------------------------------------------------------------

const ArtEtreSoiPage = () => (
  <BaseLayout>
    <ArtEtreSoi />
    <ContactSection />
  </BaseLayout>
);

const AubeParentalePage = () => (
  <BaseLayout>
    <AubeParentale />
    <ContactSection />
  </BaseLayout>
);

const ArchitecteAncragePage = () => (
  <BaseLayout>
    <ArchitecteAncrage />
    <ContactSection />
  </BaseLayout>
);

// ---------------------------------------------------------------------
// Nouvelles pages About & Investment
// ---------------------------------------------------------------------

const AboutPage = () => (
  <BaseLayout>
    <AboutSection />
    <ContactSection />
  </BaseLayout>
);

const InvestmentPage = () => (
  <BaseLayout>
    <InvestmentSection />
    <ContactSection />
  </BaseLayout>
);


// ---------------------------------------------------------------------
// App (routes)
// ---------------------------------------------------------------------

function App() {
  return (
    <div className="App">
      <BrowserRouter>
      <ScrollToTopOnRoute />
        <Routes>
          {/* Landing */}
          <Route path="/" element={<HomeInner />} />

          {/* Nouvelles pages About & Investment */}
          <Route path="/about" element={<AboutPage />} />
          <Route path="/investment" element={<InvestmentPage />} />

          {/* Services */}
          <Route path="/art-d-etre-soi" element={<ArtEtreSoiPage />} />
          <Route path="/aube-parentale" element={<AubeParentalePage />} />
          <Route
            path="/architecte-ancrage"
            element={<ArchitecteAncragePage />}
          />

          {/* Pages légales */}
          <Route path="/impressum" element={<Impressum />} />
          <Route path="/datenschutz" element={<Datenschutz />} />
          <Route path="/agb" element={<AGB />} />
          <Route path="/widerruf" element={<Widerruf />} />
          <Route path="/cookies" element={<Cookies />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}


export default App;
